import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearch } from '@tanstack/react-router';
import PageWithHeadline from '@/components/layout/PageWithHeadline';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { EditableDropdown } from '@/components/EditableDropdown';
import { MultiSelectFilter } from '@/components';
import Pagination from '@/components/tables/Pagination';
import { useSidePanelHandler } from '@/hooks/useSidePanelHandler';
import { useDeviceModelDetail, useFirmwares, useFirmwareCount } from '../../hooks';
import { FirmwareTable } from './FirmwareTable';
import { FirmwareSidePanelHandler, type FirmwareSidePanelContent } from './FirmwareSidePanel';
import type { Firmware, FirmwareQueryParams, FirmwareCountParams } from '../../types';

interface FirmwareSearchState {
  page?: number;
  pageSize?: number;
  firmwareTypeIds?: string;
  countryId?: string;
}

function setUrlSearchParams(params: FirmwareSearchState) {
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.replaceState({}, '', url.toString());
}

export function FirmwarePage() {
  const { t } = useTranslation();
  const { modelId: modelIdStr } = useParams({ strict: false }) as { modelId: string };
  const modelId = Number(modelIdStr);

  const initialSearch = useSearch({ strict: false }) as FirmwareSearchState;

  const [searchParams, setSearchParamsState] = useState<FirmwareSearchState>(initialSearch);

  const { data: modelDetail, isLoading: isLoadingModel } = useDeviceModelDetail(modelId);

  // Parse the comma-separated type ids from the URL; an empty list means "all types".
  const selectedTypeIds = useMemo(
    () =>
      searchParams.firmwareTypeIds
        ? searchParams.firmwareTypeIds.split(',').map(Number)
        : [],
    [searchParams.firmwareTypeIds]
  );

  const [selectedCountryId, setSelectedCountryId] = useState<string | null>(
    searchParams.countryId ?? null
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const page = searchParams.page || 1;
  const pageSize = searchParams.pageSize || 10;

  const queryParams: FirmwareQueryParams = useMemo(
    () => ({
      page,
      pageSize,
      firmwareTypeIds: selectedTypeIds,
      countryId: selectedCountryId,
    }),
    [page, pageSize, selectedTypeIds, selectedCountryId]
  );

  const countParams: FirmwareCountParams = useMemo(
    () => ({
      firmwareTypeIds: selectedTypeIds,
      countryId: selectedCountryId,
    }),
    [selectedTypeIds, selectedCountryId]
  );

  const { data: firmwaresData, isLoading: isLoadingFirmwares } = useFirmwares(
    modelId,
    queryParams
  );
  const { data: countData } = useFirmwareCount(modelId, countParams);

  const firmwares = firmwaresData?.data || [];
  const total = countData?.total || 0;

  const sidePanel = useSidePanelHandler<FirmwareSidePanelContent>();

  const updateSearchParams = useCallback(
    (updates: Partial<FirmwareSearchState>) => {
      const newParams: FirmwareSearchState = { ...searchParams, ...updates };
      setUrlSearchParams(newParams);
      setSearchParamsState(newParams);
    },
    [searchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: newPage });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ pageSize: newPageSize, page: 1 });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handleTypeChange = useCallback(
    (ids: number[]) => {
      updateSearchParams({
        firmwareTypeIds: ids.length > 0 ? ids.join(',') : undefined,
        page: 1,
      });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handleCountryChange = useCallback(
    (id: string | number) => {
      const countryId = String(id) === '__all__' ? null : String(id);
      setSelectedCountryId(countryId);
      updateSearchParams({ countryId: countryId ?? undefined, page: 1 });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handleFirmwareClick = useCallback(
    (firmware: Firmware) => {
      sidePanel.open({ type: 'view', firmware });
    },
    [sidePanel]
  );

  const handleAddFirmware = useCallback(() => {
    sidePanel.open({ type: 'create' });
  }, [sidePanel]);

  const firmwareTypeItems = useMemo(
    () =>
      (modelDetail?.firmwareTypes || []).map((ft) => ({
        id: ft.id,
        label: ft.name,
      })),
    [modelDetail]
  );

  // Map of type id to name, the shape the MultiSelectFilter popup expects.
  const firmwareTypeRecord = useMemo(
    () =>
      Object.fromEntries((modelDetail?.firmwareTypes ?? []).map((ft) => [ft.id, ft.name])),
    [modelDetail]
  );

  const countryItems = useMemo(() => {
    const globalEntry = { id: '__all__' as string | number, label: t('firmware.filters.global') };
    const countryEntries = (modelDetail?.countries || []).map((c) => ({
      id: c.countryID as string | number,
      label: c.countryID,
    }));
    return [globalEntry, ...countryEntries];
  }, [modelDetail, t]);

  const modelName = modelDetail?.name || '';

  return (
    <PageWithHeadline 
      headlineKey="firmware.title"
    >
      {!isLoadingModel && modelName && (
        <span className="text-lg font-semibold text-muted-foreground">{modelName}</span>
      )}
      
      <PageContentCard className="flex-1 min-h-0 flex flex-col w-full space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <MultiSelectFilter
            items={firmwareTypeRecord}
            selectedIds={selectedTypeIds}
            onChange={handleTypeChange}
            buttonLabel={t('firmware.filters.type')}
            emptyLabel={t('firmware.filters.noTypes')}
            isLoading={isLoadingModel}
          />

          <EditableDropdown
            items={countryItems}
            selectedId={selectedCountryId ?? '__all__'}
            onSelect={handleCountryChange}
            placeholder={t('firmware.filters.country')}
          />

          <div className="ml-auto">
            <button
              type="button"
              onClick={handleAddFirmware}
              className="py-2 px-3 inline-flex items-center gap-x-1.5 text-sm font-medium rounded-lg bg-primary text-onprimary shadow-2xs hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:ring-2 focus:ring-primary"
            >
              <svg
                className="shrink-0 size-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              {t('firmware.addButton')}
            </button>
          </div>
        </div>

        
          <FirmwareTable
            firmwares={firmwares}
            isLoading={isLoadingFirmwares}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onFirmwareClick={handleFirmwareClick}
            firmwareTypes={modelDetail?.firmwareTypes || []}
        countries={modelDetail?.countries || []}
          />
        

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </PageContentCard>

      <FirmwareSidePanelHandler
        content={sidePanel.content}
        panelKey={sidePanel.panelKey}
        isOpen={sidePanel.isOpen}
        onClose={sidePanel.close}
        modelId={modelId}
        firmwareTypes={modelDetail?.firmwareTypes || []}
        countries={modelDetail?.countries || []}
        firmwareTypeItems={firmwareTypeItems}
        currentTypeId={selectedTypeIds[0] ?? modelDetail?.firmwareTypes[0]?.id}
        currentCountryId={selectedCountryId ?? undefined}
      />
    </PageWithHeadline>
  );
}
